import { preciseCurrencyFormatter } from '../../../helpers/format-currency';
import { formatCases } from '../../../helpers/format-quantity';

export type SavingsLevel = 'NONE' | 'EQUAL' | 'LOW' | 'HIGH' | 'VERY_HIGH';

export type Deal = {
  cost?: number;
  min_cases: number;
};

export const computeBars = (
  previousCost: Deal,
  currentCost: Deal,
  futureCost: Deal
): SavingsLevel[] => {
  const costs = [
    { value: previousCost.cost, min_cases: previousCost.min_cases, index: 0 },
    { value: currentCost.cost, min_cases: currentCost.min_cases, index: 1 },
    { value: futureCost.cost, min_cases: futureCost.min_cases, index: 2 },
  ];
  const definedCosts = costs.filter((cost) => cost.value !== undefined);

  if (definedCosts.length === 3) {
    // All three costs defined - use relative comparison
    const costs = [
      previousCost.cost as number,
      currentCost.cost as number,
      futureCost.cost as number,
    ];
    const minCost = Math.min(...costs);
    const maxCost = Math.max(...costs);

    const mins = [previousCost.min_cases, currentCost.min_cases, futureCost.min_cases];

    const minMin = Math.min(...mins);
    const maxMin = Math.max(...mins);

    // If all costs are the same, and all minimums are the same, all equal
    if (minCost === maxCost && minMin === maxMin) {
      return ['EQUAL', 'EQUAL', 'EQUAL'] as SavingsLevel[];
    }

    if (minCost === maxCost) {
      // If all costs are the same, differentiate deals by min cases, lower is better
      return computeBars(
        { cost: mins[0], min_cases: 0 },
        { cost: mins[1], min_cases: 0 },
        { cost: mins[2], min_cases: 0 }
      );
    }

    // Check if we have exactly 2 different values or 3 different values
    const uniqueValues = [...new Set(costs)];
    const hasThreeUniqueValues = uniqueValues.length === 3;

    return costs.map((cost) => {
      if (cost === minCost) {
        return 'LOW';
      }

      if (cost === maxCost && hasThreeUniqueValues) {
        return 'VERY_HIGH';
      }

      return 'HIGH';
    }) as SavingsLevel[];
  }

  const levels: SavingsLevel[] = ['NONE', 'NONE', 'NONE'];

  if (definedCosts.length === 1) {
    // Only one cost defined - make it green, others stay gray
    levels[definedCosts[0].index] = 'LOW';
  } else if (definedCosts.length === 2) {
    // Two costs defined - compare them
    const [first, second] = definedCosts;
    const firstValue = first.value as number;
    const secondValue = second.value as number;

    if (firstValue < secondValue) {
      levels[first.index] = 'LOW';
      levels[second.index] = 'HIGH';
    } else if (firstValue > secondValue) {
      levels[first.index] = 'HIGH';
      levels[second.index] = 'LOW';
    } else {
      // Equal values - tie-break by min cases, lower is better
      if (first.min_cases < second.min_cases) {
        levels[first.index] = 'LOW';
        levels[second.index] = 'HIGH';
      } else if (first.min_cases > second.min_cases) {
        levels[first.index] = 'HIGH';
        levels[second.index] = 'LOW';
      } else {
        levels[first.index] = 'LOW';
        levels[second.index] = 'LOW';
      }
    }
  }

  return levels;
};

type SavingsTooltipContentProps = {
  costsData: Array<{ month: string; deal: Deal }>;
};

export function SavingsTooltipContent({ costsData }: SavingsTooltipContentProps) {
  if (costsData.every(({ deal }) => deal.cost === undefined)) {
    return;
  }

  const allCostsEqual = costsData.every(({ deal }) => deal.cost === costsData[0].deal.cost);
  const allMinsEqual = costsData.every(
    ({ deal }) => deal.min_cases === costsData[0].deal.min_cases
  );
  const hasMinQty = costsData.some(({ deal }) => deal.min_cases > 0);

  // Simplified display if all costs are the same
  if (allCostsEqual && allMinsEqual && costsData[0].deal.cost !== undefined) {
    return <div className="text-center">{preciseCurrencyFormatter(costsData[0].deal.cost)}</div>;
  }

  return (
    <div className={hasMinQty ? 'w-64' : 'w-48'}>
      <div className="flex flex-col divide-y divide-popover-border">
        <div className="flex flex-row pb-1">
          <div
            className={`${hasMinQty ? 'basis-1/3' : 'basis-1/2'} flex-none font-semibold text-sm`}
          >
            Month
          </div>
          <div
            className={`${hasMinQty ? 'basis-1/3' : 'basis-1/2'} flex-none font-semibold text-sm text-right`}
          >
            Cost
          </div>
          {hasMinQty && (
            <div className="basis-1/3 flex-none font-semibold text-sm text-right">Min Qty</div>
          )}
        </div>
        {costsData.map(({ month, deal }) => (
          <div key={month} className="flex flex-row p-1">
            <div className={`${hasMinQty ? 'basis-1/3' : 'basis-1/2'} flex-none`}>{month}</div>
            <div
              className={`${hasMinQty ? 'basis-1/3' : 'basis-1/2'} flex-none text-right tabular-nums`}
            >
              {deal.cost !== undefined ? preciseCurrencyFormatter(deal.cost) : '-'}
            </div>
            {hasMinQty && (
              <div className="basis-1/3 flex-none text-right tabular-nums">
                {deal.min_cases > 0 ? formatCases(deal.min_cases) : ''}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
